import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import Cookies from "js-cookie";
import { userService } from "../services/user";
import { BaseGetRequestParams } from "@quest/types";
import { adminService } from "@quest/shared/member/services";
import { useAppSelector } from "@quest/hooks";

const PREVIEW_REJECT = () => Promise.reject(new Error("Read-only preview"));

export const useUser = () => {
  const hasToken = !!Cookies.get("accessToken");

  return useQuery({
    queryKey: ["user"],
    queryFn: () => userService.getUser(),
    retry: false,
    enabled: hasToken,
    staleTime: 5 * 60 * 1000,
  });
};

export const useStudentDetailsByIdpId = (idpId: string, enabled: boolean) => {
  return useQuery({
    queryKey: ["student-details", idpId],

    queryFn: () => {
      if (!idpId) return Promise.resolve(null);
      return userService.getStudentDetailsByIdpId(idpId);
    },

    retry: false,
    enabled: !!enabled && idpId !== "null",
  });
};

export const useUserPermissions = (roleName: string) => {
  return useQuery({
    queryKey: ["user-permissions", roleName],
    queryFn: () => userService.getUserPermissions(roleName),
    retry: false,
    enabled: !!roleName,
  });
};

export const useUserDenials = (tenantSlug: string, enabled: boolean) => {
  return useQuery({
    queryKey: ["user-denials", tenantSlug],
    queryFn: () => adminService.getUserDenials(tenantSlug),
    retry: false,
    enabled: !!tenantSlug && enabled,
  });
};

export const useUpdateUserProfile = () => {
  const queryClient = useQueryClient();
  const isPreviewMode = useAppSelector((state) =>
    Boolean(state.preview?.active)
  );
  return useMutation({
    mutationKey: ["update-user-profile"],
    mutationFn: ({
      profile_image,
      idpId,
    }: {
      profile_image: string | null;
      idpId: string;
    }) =>
      isPreviewMode
        ? PREVIEW_REJECT()
        : userService.updateUserProfile(profile_image, idpId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["user"] });
    },
  });
};

export const useGetNotificationUnreadCount = () => {
  return useQuery({
    queryKey: ["notification-unread-count"],
    queryFn: () => userService.getNotificationUnreadCount(),
    retry: false,
    staleTime: 0,
    refetchOnWindowFocus: true,
  });
};

export const useGetMentorList = (
  params: BaseGetRequestParams,
  enabled: boolean
) => {
  return useInfiniteQuery({
    queryKey: ["getMentorList", params],
    queryFn: ({ pageParam }) =>
      userService.getMentorList({ ...params, page: pageParam }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      return lastPage?.pagination?.hasNext
        ? lastPage?.pagination?.page + 1
        : undefined;
    },
    enabled: enabled,
  });
};

export const useGetAdminList = (
  params: BaseGetRequestParams,
  enabled: boolean
) => {
  return useInfiniteQuery({
    queryKey: ["getAdminList", params],
    queryFn: ({ pageParam }) =>
      userService.getAdminList({ ...params, page: pageParam }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      return lastPage?.pagination?.hasNext
        ? lastPage?.pagination?.page + 1
        : undefined;
    },
    enabled: enabled,
  });
};

export const useLogout = () => {
  return useMutation({
    mutationFn: (access_token: string) => userService.logout(access_token),
  });
};
